import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSignatures, useDeleteSignature } from '@/hooks/useSignatures'
import { Signature } from '@/types'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  AlertCircle,
  Copy,
  Search,
  MoreHorizontal
} from 'lucide-react'
import { format } from 'date-fns'
import { useToast } from '@/hooks/use-toast'
import SignatureForm from '@/components/forms/SignatureForm'
import { OperatorReportDetails } from '@/components/OperatorReportDetails'
import { OperatorReportStatusDisplay } from '@/components/OperatorReportStatusDisplay'

const statusMap = {
  PENDING: { label: '审核中', variant: 'warning' as const, icon: Clock, color: 'text-yellow-600' },
  APPROVED: { label: '已通过', variant: 'success' as const, icon: CheckCircle, color: 'text-green-600' },
  REJECTED: { label: '已拒绝', variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' },
  DISABLED: { label: '已禁用', variant: 'secondary' as const, icon: XCircle, color: 'text-gray-600' },
}

// 平台审核状态映射（独立显示）
const getPlatformStatus = (signature: Signature) => {
  switch (signature.status) {
    case 'PENDING':
      return { label: '审核中', variant: 'warning' as const, icon: Clock, color: 'text-yellow-600' }
    case 'APPROVED':
      return { label: '可使用', variant: 'success' as const, icon: CheckCircle, color: 'text-green-600' }
    case 'REJECTED':
      return { label: '已拒绝', variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' }
    default:
      return { label: '未知状态', variant: 'secondary' as const, icon: AlertCircle, color: 'text-gray-600' }
  }
}

// 运营商报备状态映射（独立显示）
const getOperatorReportStatus = (signature: Signature) => {
  switch (signature.operatorReportStatus) {
    case 'NOT_REPORTED':
      return { label: '未报备', variant: 'secondary' as const, icon: AlertCircle, color: 'text-gray-600' }
    case 'REPORTING':
      return { label: '报备中', variant: 'warning' as const, icon: Clock, color: 'text-yellow-600' }
    case 'REPORTED':
      return { label: '已报备', variant: 'success' as const, icon: CheckCircle, color: 'text-green-600' }
    case 'REJECTED':
      return { label: '报备被拒', variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' }
    default:
      return { label: '未报备', variant: 'secondary' as const, icon: AlertCircle, color: 'text-gray-600' }
  }
}

// 兼容旧的状态显示逻辑（用于筛选等）
const getDisplayStatus = (signature: Signature) => {
  // 如果平台审核未通过，直接返回平台状态
  if (signature.status !== 'APPROVED') {
    return getPlatformStatus(signature)
  }
  
  // 如果平台审核通过，检查运营商报备状态
  switch (signature.operatorReportStatus) {
    case 'REPORTED':
      return { label: '可使用', variant: 'success' as const, icon: CheckCircle, color: 'text-green-600' }
    case 'REPORTING':
      return { label: '报备中', variant: 'warning' as const, icon: Clock, color: 'text-yellow-600' }
    case 'REJECTED':
      return { label: '报备失败', variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' }
    case 'NOT_REPORTED':
    default:
      return { label: '未报备', variant: 'secondary' as const, icon: AlertCircle, color: 'text-gray-600' }
  }
}

const operatorReportStatusMap = {
  NOT_REPORTED: { label: '未报备', variant: 'secondary' as const, color: 'text-gray-600' },
  REPORTING: { label: '报备中', variant: 'warning' as const, color: 'text-yellow-600' },
  REPORTED: { label: '已报备', variant: 'success' as const, color: 'text-green-600' },
  REJECTED: { label: '报备被拒', variant: 'destructive' as const, color: 'text-red-600' },
}

const typeMap = {
  COMPANY: { label: '公司', variant: 'default' as const, icon: '🏢' },
  WEBSITE: { label: '网站', variant: 'secondary' as const, icon: '🌐' },
  APP: { label: 'APP', variant: 'outline' as const, icon: '📱' },
  WECHAT: { label: '微信', variant: 'default' as const, icon: '💬' },
  BRAND: { label: '品牌', variant: 'secondary' as const, icon: '🏷️' },
  TRADEMARK: { label: '商标', variant: 'outline' as const, icon: '®️' },
  GOVERNMENT: { label: '政府', variant: 'default' as const, icon: '🏛️' },
}

export default function Signatures() {
  const [selectedSignature, setSelectedSignature] = useState<Signature | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const { toast } = useToast()
  const { data: signaturesData, isLoading, refetch } = useSignatures({ page: 0, size: 10 })
  const deleteSignatureMutation = useDeleteSignature()

  // 过滤数据
  const filteredSignatures = (signaturesData?.content || []).filter(signature => {
    const matchesSearch = signature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         signature.content.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || getDisplayStatus(signature).label === statusFilter
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'approved' && getDisplayStatus(signature).label === '可使用') ||
                      (activeTab === 'pending' && (getDisplayStatus(signature).label === '审核中' || getDisplayStatus(signature).label === '报备中' || getDisplayStatus(signature).label === '未报备')) ||
                      (activeTab === 'rejected' && (getDisplayStatus(signature).label === '已拒绝' || getDisplayStatus(signature).label === '报备失败'))
    
    return matchesSearch && matchesStatus && matchesTab
  })

  const handleDelete = async (signature: Signature) => {
    if (confirm(`确定要删除签名 "${signature.name}" 吗？`)) {
      try {
        await deleteSignatureMutation.mutateAsync(signature.id)
        toast({
          title: '删除成功',
          description: `签名 "${signature.name}" 已被删除`,
        })
      } catch (error) {
        toast({
          title: '删除失败',
          description: '请稍后重试',
          variant: 'destructive',
        })
      }
    }
  }

  const handleCopySignature = (content: string) => {
    navigator.clipboard.writeText(content)
    toast({
      title: '已复制',
      description: '签名格式已复制到剪贴板',
    })
  }

  const columns: ColumnDef<Signature>[] = [
    {
      accessorKey: 'name',
      header: '签名名称',
      cell: ({ row }) => {
        const signature = row.original
        return (
          <div className="flex items-center space-x-2">
            <div>
              <div className="font-medium">{signature.name}</div>
              <div className="text-sm text-muted-foreground">
                {typeMap[signature.type as keyof typeof typeMap]?.icon} {typeMap[signature.type as keyof typeof typeMap]?.label}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'content',
      header: '签名内容',
      cell: ({ row }) => {
        const content = row.getValue('content') as string
        return (
          <div className="flex items-center space-x-2">
            <span className="font-mono bg-gray-100 px-2 py-1 rounded">{content}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopySignature(content)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: '审核状态',
      cell: ({ row }) => {
        const status = getPlatformStatus(row.original)
        const Icon = status.icon
        return (
          <Badge variant={status.variant} className="flex items-center gap-1">
            <Icon className="h-3 w-3" />
            {status.label}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'operatorReports',
      header: '运营商报备',
      cell: ({ row }) => {
        const operatorReports = row.original.operatorReports
        return <OperatorReportStatusDisplay operatorReports={operatorReports} compact={true} />
      },
    },
    {
      accessorKey: 'usageCount',
      header: '使用次数',
      cell: ({ row }) => {
        const usageCount = row.getValue('usageCount') as number || 0
        const lastUsedAt = row.original.lastUsedAt
        return (
          <div className="text-sm">
            <div className="font-medium">{usageCount.toLocaleString()}</div>
            {lastUsedAt && (
              <div className="text-muted-foreground">
                最后使用: {format(new Date(lastUsedAt), 'MM-dd HH:mm')}
              </div>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: '创建时间',
      cell: ({ row }) => {
        return format(new Date(row.getValue('createdAt')), 'yyyy-MM-dd HH:mm')
      },
    },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        const signature = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedSignature(signature)
                  setShowDetailDialog(true)
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                查看详情
              </DropdownMenuItem>
              {signature.status !== 'APPROVED' && (
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedSignature(signature)
                    setShowEditDialog(true)
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  编辑
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => handleDelete(signature)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]



  return (
    <div className="flex-1 space-y-6 p-8 pt-6">
      {/* 页面标题和操作 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">签名管理</h2>
          <p className="text-muted-foreground">
            管理您的短信签名，支持多种类型和用途
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            刷新
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                申请签名
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>申请新签名</DialogTitle>
                <DialogDescription>
                  创建新的短信签名，提交后等待审核
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar" style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#d1d5db #f3f4f6'
              }}>
                <SignatureForm
                  onSuccess={() => {
                    setShowCreateDialog(false)
                    toast({
                      title: '申请成功',
                      description: '签名已提交审核，请耐心等待',
                    })
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>



      {/* 快速操作提示 */}
      {(signaturesData?.content || []).filter(s => getDisplayStatus(s).label === '可使用').length === 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <div>
                <h4 className="font-medium text-orange-900">还没有可用的签名</h4>
                <p className="text-sm text-orange-800">
                  请先申请签名并通过审核，然后才能发送短信
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 签名列表 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>我的签名</CardTitle>
              <CardDescription>
                管理您的短信签名和审核状态
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜索签名..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  <SelectItem value="可使用">可使用</SelectItem>
                  <SelectItem value="审核中">审核中</SelectItem>
                  <SelectItem value="报备中">报备中</SelectItem>
                  <SelectItem value="未报备">未报备</SelectItem>
                  <SelectItem value="已拒绝">已拒绝</SelectItem>
                  <SelectItem value="报备失败">报备失败</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all">全部 ({signaturesData?.totalElements || 0})</TabsTrigger>
              <TabsTrigger value="approved">可使用 ({(signaturesData?.content || []).filter(s => getDisplayStatus(s).label === '可使用').length})</TabsTrigger>
              <TabsTrigger value="pending">审核中 ({(signaturesData?.content || []).filter(s => getDisplayStatus(s).label === '审核中' || getDisplayStatus(s).label === '报备中' || getDisplayStatus(s).label === '未报备').length})</TabsTrigger>
              <TabsTrigger value="rejected">已拒绝 ({(signaturesData?.content || []).filter(s => getDisplayStatus(s).label === '已拒绝' || getDisplayStatus(s).label === '报备失败').length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-sm text-muted-foreground">加载中...</div>
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredSignatures}
                  searchKey="name"
                  searchPlaceholder="搜索签名名称..."
                />
              )}
            </TabsContent>
            
            <TabsContent value="approved" className="mt-4">
              <DataTable
                columns={columns}
                data={(signaturesData?.content || []).filter(s => getDisplayStatus(s).label === '可使用')}
                searchKey="name"
                searchPlaceholder="搜索可使用的签名..."
              />
            </TabsContent>
            
            <TabsContent value="pending" className="mt-4">
              <DataTable
                columns={columns}
                data={(signaturesData?.content || []).filter(s => getDisplayStatus(s).label === '审核中' || getDisplayStatus(s).label === '报备中' || getDisplayStatus(s).label === '未报备')}
                searchKey="name"
                searchPlaceholder="搜索审核中的签名..."
              />
            </TabsContent>
            
            <TabsContent value="rejected" className="mt-4">
              <DataTable
                columns={columns}
                data={(signaturesData?.content || []).filter(s => getDisplayStatus(s).label === '已拒绝' || getDisplayStatus(s).label === '报备失败')}
                searchKey="name"
                searchPlaceholder="搜索已拒绝的签名..."
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* 编辑签名对话框 */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>编辑签名</DialogTitle>
            <DialogDescription>
              修改签名信息
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar" style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db #f3f4f6'
          }}>
            {selectedSignature && (
              <SignatureForm
                signature={selectedSignature}
                onSuccess={() => {
                  setShowEditDialog(false)
                  setSelectedSignature(null)
                  toast({
                    title: '更新成功',
                    description: '签名信息已更新',
                  })
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 签名详情对话框 */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>签名详情</DialogTitle>
          </DialogHeader>
          {selectedSignature && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>签名名称</Label>
                  <p className="text-sm mt-1">{selectedSignature.name}</p>
                </div>
                <div>
                  <Label>签名类型</Label>
                  <p className="text-sm mt-1">
                    <Badge variant={typeMap[selectedSignature.type].variant}>
                      {typeMap[selectedSignature.type].icon} {typeMap[selectedSignature.type].label}
                    </Badge>
                  </p>
                </div>
              </div>
              
              <div>
                <Label>签名内容</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm font-mono bg-gray-50 p-3 rounded border flex-1">
                    {selectedSignature.content}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopySignature(selectedSignature.content)}
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    复制
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>审核状态</Label>
                  <p className="text-sm mt-1">
                    <Badge variant={getDisplayStatus(selectedSignature).variant}>
                      {getDisplayStatus(selectedSignature).label}
                    </Badge>
                  </p>
                </div>
                <div>
                  <Label>运营商报备</Label>
                  <p className="text-sm mt-1">
                    {selectedSignature.operatorReportStatus ? (
                      <Badge variant={operatorReportStatusMap[selectedSignature.operatorReportStatus as keyof typeof operatorReportStatusMap]?.variant || 'secondary'} className="text-xs">
                        {operatorReportStatusMap[selectedSignature.operatorReportStatus as keyof typeof operatorReportStatusMap]?.label || '未知状态'}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>使用次数</Label>
                  <p className="text-sm mt-1 font-medium">
                    {(selectedSignature.usageCount || 0).toLocaleString()}
                  </p>
                </div>
                {selectedSignature.operatorReportTime && (
                  <div>
                    <Label>报备时间</Label>
                    <p className="text-sm mt-1">
                      {format(new Date(selectedSignature.operatorReportTime), 'yyyy-MM-dd HH:mm:ss')}
                    </p>
                  </div>
                )}
              </div>
              
              {selectedSignature.certificate && (
                <div>
                  <Label>资质证明</Label>
                  <p className="text-sm mt-1">
                    <Button variant="outline" size="sm" className="text-blue-600">
                      <FileText className="h-4 w-4 mr-1" />
                      查看证明文件
                    </Button>
                  </p>
                </div>
              )}
              
              {selectedSignature.reviewComment && (
                <div>
                  <Label>审核意见</Label>
                  <p className="text-sm mt-1 text-red-600 bg-red-50 p-3 rounded">
                    {selectedSignature.reviewComment}
                  </p>
                </div>
              )}
              
              {/* 运营商报备详情 */}
              {selectedSignature.operatorReports && selectedSignature.operatorReports.length > 0 && (
                <div>
                  <OperatorReportDetails operatorReports={selectedSignature.operatorReports} />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                <div>
                  <Label>创建时间</Label>
                  <p className="mt-1">{format(new Date(selectedSignature.createdAt), 'yyyy-MM-dd HH:mm:ss')}</p>
                </div>
                {selectedSignature.lastUsedAt && (
                  <div>
                    <Label>最后使用</Label>
                    <p className="mt-1">{format(new Date(selectedSignature.lastUsedAt), 'yyyy-MM-dd HH:mm:ss')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}